<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Solicitudes de vacaciones</h1>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>

        <template #right>
          <wz-button v-if="selectedIds.length > 0" variant="danger" @click="openBatchAnnul()">
            <i class="ri-close-circle-line mr-1"></i>{{ selectedIds.length > 1 ? `Anular ${selectedIds.length} seleccionadas` : 'Anular' }}
          </wz-button>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-tooltip text="Descargar">
            <wz-button variant="outline" @click="downloadReport()">
              <i class="ri-download-2-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por nombre o número de documento"
            @search="onSearch"
            @input="search = $event"
          />
          <wz-button variant="outline" @click="$router.push({ name: 'vacation-requests-historical' })">
            <i class="ri-history-line mr-1"></i>Registrar histórico
          </wz-button>
          <wz-button variant="outline" @click="$router.push({ name: 'vacation-bulk-upload' })">
            <i class="ri-upload-2-line mr-1"></i>Carga masiva
          </wz-button>
          <wz-button variant="primary" @click="$router.push({ name: 'vacation-requests-create' })">Agregar</wz-button>
        </template>

        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th style="width: 36px; text-align: center;">
              <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" title="Seleccionar todos" />
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'employeeName' }" @click="toggleSort('employeeName')">
              Colaborador <i :class="['wz-th-sort-icon', sortIcon('employeeName')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'companyName' }" @click="toggleSort('companyName')">
              Empresa <i :class="['wz-th-sort-icon', sortIcon('companyName')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'areaName' }" @click="toggleSort('areaName')">
              Área <i :class="['wz-th-sort-icon', sortIcon('areaName')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'periodStartDate' }" @click="toggleSort('periodStartDate')">
              Período <i :class="['wz-th-sort-icon', sortIcon('periodStartDate')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'startDate' }" @click="toggleSort('startDate')">
              Fechas <i :class="['wz-th-sort-icon', sortIcon('startDate')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'calendarDays' }" @click="toggleSort('calendarDays')">
              Días <i :class="['wz-th-sort-icon', sortIcon('calendarDays')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'status' }" @click="toggleSort('status')">
              Estado <i :class="['wz-th-sort-icon', sortIcon('status')]"></i>
            </th>
            <th>Pago</th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="list.length === 0">
            <td colspan="10" class="text-center text-muted py-4">No se encontraron solicitudes</td>
          </tr>
          <tr v-for="item in sortedList(list)" :key="item.id">
            <td style="text-align: center;">
              <input type="checkbox" :value="item.id" v-model="selectedIds" />
            </td>
            <td>
              {{ item.employeeName || '-' }}
              <wz-badge v-if="item.requestType === 'VENTA'" variant="secondary" class="badge-purple ml-1" pill>Venta</wz-badge>
            </td>
            <td>{{ item.companyName || '-' }}</td>
            <td>{{ item.areaName || '-' }}</td>
            <td style="white-space: nowrap;">{{ item.periodLabel || '-' }}</td>
            <td style="white-space: nowrap;">{{ item.requestType === 'VENTA' ? '—' : formatRange(item.startDate, item.endDate) }}</td>
            <td>{{ item.requestType === 'VENTA' ? (item.soldDays || item.calendarDays) : (item.calendarDays || '-') }}</td>
            <!-- Columna ESTADO: un solo badge con el estado real -->
            <td>
              <wz-badge :variant="statusVariant(item.status)"
                        :class="{ 'badge-purple': item.status === 'GOZADA' }" pill>
                {{ item.status }}
              </wz-badge>
            </td>
            <!-- Columna PAGO: solo APROBADA / GOZADA -->
            <td>
              <wz-badge v-if="showPaymentBadge(item)"
                        :variant="item.isPaid ? 'info-solid' : 'secondary'"
                        :class="{ 'badge-unpaid': !item.isPaid }" pill>
                {{ item.isPaid ? 'PAGADA' : 'SIN PAGAR' }}
              </wz-badge>
              <span v-else class="text-muted">—</span>
            </td>
            <td style="text-align: center;">
              <wz-dropdown
                :items="requestActions(item)"
                placement="left"
                @click="handleAction(item, $event)"
              />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <span>{{ list.length }} registro(s) encontrado(s)</span>
      </wz-table-footer>

    </wz-table-container>

    <!-- Modal: Editar (fechas + observaciones) -->
    <wz-modal v-model="modalEdit" title="Editar solicitud">
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-datepicker label="Fecha de inicio" required v-model="editForm.startDate" />
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-datepicker label="Fecha de fin" required v-model="editForm.endDate" />
        </div>
      </div>
      <div v-if="editCalendarDays !== null" class="mb-3">
        <wz-badge variant="success-solid">{{ editCalendarDays }} días calendario</wz-badge>
      </div>
      <div class="mb-1">
        <wz-textarea label="Observaciones" v-model="editForm.observations" rows="3" placeholder="Observaciones (opcional)" />
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalEdit = false">Cancelar</wz-button>
        <wz-button :disabled="!editValid" @click="onEditSubmit">Guardar cambios</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: Aprobar -->
    <wz-modal v-model="showApprove" size="sm" title="Aprobar solicitud" :close-on-overlay="false">
      <div class="confirm-body">
        <div class="confirm-icon confirm-icon--success">
          <i class="ri-checkbox-circle-line"></i>
        </div>
        <p class="confirm-question">¿Confirma que desea aprobar esta solicitud?</p>
        <template v-if="selectedItem">
          <p class="confirm-detail"><strong>{{ selectedItem.employeeName }}</strong></p>
          <p class="confirm-detail"><span v-local-date="selectedItem.startDate"></span> al <span v-local-date="selectedItem.endDate"></span> ({{ selectedItem.calendarDays }} días)</p>
        </template>
      </div>
      <template #footer>
        <wz-button variant="outline" @click="showApprove = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="confirmApprove">Aprobar</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: Rechazar -->
    <wz-modal v-model="showReject" size="sm" title="Rechazar solicitud" :close-on-overlay="false">
      <wz-textarea
        v-model="rejectReason"
        label="Motivo de rechazo"
        placeholder="Ingrese el motivo del rechazo"
        :rows="3"
        required
      />
      <template #footer>
        <wz-button variant="outline" @click="showReject = false">Cancelar</wz-button>
        <wz-button variant="danger" :disabled="!rejectReason.trim()" @click="confirmReject">Rechazar</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: Cancelar -->
    <wz-modal v-model="showCancel" size="sm" title="Cancelar solicitud" :close-on-overlay="false">
      <wz-textarea
        v-model="cancelReason"
        label="Motivo de cancelación"
        placeholder="Ingrese el motivo de la cancelación"
        :rows="3"
        required
      />
      <template #footer>
        <wz-button variant="outline" @click="showCancel = false">Volver</wz-button>
        <wz-button variant="danger" :disabled="!cancelReason.trim()" @click="confirmCancel">Cancelar solicitud</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: Anular por error -->
    <annul-by-mistake-modal
      v-model="showAnnul"
      :submitting="annulSubmitting"
      :entity-description="selectedItem ? `${selectedItem.employeeName} — vacaciones del ${selectedItem.startDate} al ${selectedItem.endDate}` : ''"
      @confirm="doAnnul"
    />

    <!-- Modal: Anular por error (masivo) -->
    <annul-by-mistake-modal
      v-model="showBatchAnnul"
      :submitting="batchSubmitting"
      :entity-descriptions="batchDescriptions"
      @confirm="doBatchAnnul"
    />

    <!-- Modal: Marcar como gozada -->
    <wz-modal v-model="showEnjoy" size="sm" title="Marcar como gozada" :close-on-overlay="false">
      <div class="confirm-body">
        <div class="confirm-icon confirm-icon--success">
          <i class="ri-calendar-check-line"></i>
        </div>
        <p class="confirm-question">¿Marcar estas vacaciones como gozadas?</p>
        <template v-if="selectedItem">
          <p class="confirm-detail"><strong>{{ selectedItem.employeeName }}</strong></p>
          <p class="confirm-detail"><span v-local-date="selectedItem.startDate"></span> al <span v-local-date="selectedItem.endDate"></span> ({{ selectedItem.calendarDays }} días)</p>
        </template>
      </div>
      <template #footer>
        <wz-button variant="outline" @click="showEnjoy = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="confirmEnjoy">Confirmar</wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { FilterMixin } from "@wizepe/wizeui"
import { SortMixin } from '@wizepe/wizeui'
import { VacationService, CompanyService } from '@/core/services'
import AnnulByMistakeModal from '@/core/components/AnnulByMistakeModal.vue'
import * as moment from 'moment'

export default {
  name: 'VacationRequestList',
  components: { AnnulByMistakeModal },
  mixins: [SortMixin, FilterMixin],
  data() {
    return {
      list: [],
      search: '',
      selectedItem: null,
      selectedIds: [],
      showBatchAnnul: false,
      batchSubmitting: false,
      rejectReason: '',
      cancelReason: '',
      showApprove: false,
      showReject: false,
      showCancel: false,
      showEnjoy: false,
      showAnnul: false,
      annulSubmitting: false,
      modalEdit: false,
      editForm: { startDate: '', endDate: '', observations: '' },
      filterOptions: [
        {
          label: 'Estado',
          key: 'status',
          type: 'SINGLE',
          options: [
            { key: '', label: 'Todos' },
            { key: 'PENDIENTE', label: 'Pendiente' },
            { key: 'APROBADA', label: 'Aprobada' },
            { key: 'GOZADA', label: 'Gozada' },
            { key: 'CANCELADA', label: 'Cancelada' },
            { key: 'RECHAZADA', label: 'Rechazada' },
          ],
        },
        {
          label: 'Pago',
          key: 'paid',
          type: 'SINGLE',
          options: [
            { key: 'PAID', label: 'Pagadas' },
            { key: 'UNPAID', label: 'Sin pagar' },
          ],
        },
        {
          label: 'Tipo',
          key: 'tipo',
          type: 'SINGLE',
          options: [
            { key: 'REGULAR', label: 'Regular' },
            { key: 'HISTORICAL', label: 'Histórico' },
            { key: 'VENTA', label: 'Venta' },
          ],
        },
        {
          label: 'Empresa',
          key: 'companyId',
          type: 'SINGLE',
          options: [],
        },
      ],
    }
  },
  computed: {
    editCalendarDays() {
      if (!this.editForm.startDate || !this.editForm.endDate) return null
      const start = new Date(this.editForm.startDate + 'T00:00:00')
      const end   = new Date(this.editForm.endDate   + 'T00:00:00')
      const diff  = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
      return diff > 0 ? diff : null
    },
    editValid() {
      return !!(this.editForm.startDate && this.editForm.endDate && this.editCalendarDays > 0)
    },
    allSelected() {
      return this.list.length > 0 && this.list.every(i => this.selectedIds.includes(i.id))
    },
    batchDescriptions() {
      return this.list
        .filter(i => this.selectedIds.includes(i.id))
        .map(i => `${i.employeeName} — vacaciones del ${i.startDate} al ${i.endDate}`)
    },
  },
  methods: {
    toggleSelectAll(e) {
      this.selectedIds = e.target.checked ? this.list.map(i => i.id) : []
    },
    openBatchAnnul() {
      if (this.selectedIds.length > 0) this.showBatchAnnul = true
    },
    async doBatchAnnul(reason) {
      try {
        this.batchSubmitting = true
        const resp = await VacationService.annulBatch(this.selectedIds, reason)
        const payload = resp.payload || resp
        const ok = (payload.succeeded || []).length
        const failed = payload.failed || []
        const warnings = payload.warnings || []
        if (failed.length === 0) {
          Alert.success(`${ok} vacación(es) anulada(s) correctamente`)
        } else {
          const detail = failed.map(f => `#${f.id}: ${f.error}`).join('\n')
          Alert.alert('Advertencia', `${ok} anulada(s), ${failed.length} fallaron.\n${detail}`)
        }
        // Ventas cuya compensación ya se pagó en una planilla cerrada: se anularon, pero requieren descuento manual.
        if (warnings.length) {
          const wdetail = warnings
            .map(w => `${w.employeeName || '#' + w.vacationRequestId}: ${w.message}`).join('\n')
          Alert.alert('Advertencia', `${warnings.length} venta(s) tienen compensación ya pagada:\n${wdetail}`)
        }
        this.showBatchAnnul = false
        this.selectedIds = []
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al anular las selecciones')
      } finally {
        this.batchSubmitting = false
      }
    },
    statusVariant(status) {
      const map = {
        PENDIENTE: 'warning-solid',
        APROBADA:  'success-solid',
        GOZADA:    'secondary',     // color real vía clase .badge-purple
        RECHAZADA: 'danger-solid',
        CANCELADA: 'dark',
      }
      return map[status] || 'dark'
    },
    formatRange(start, end) {
      if (!start) return '-'
      const s = moment(start).format('DD/MM/YYYY')
      const e = end ? moment(end).format('DD/MM/YYYY') : ''
      return e ? `${s} — ${e}` : s
    },
    showPaymentBadge(item) {
      return item.status === 'APROBADA' || item.status === 'GOZADA'
    },
    canPay(item) {
      return !item.isPaid && this.showPaymentBadge(item)
    },
    requestActions(item) {
      const actions = [{ key: 'view', label: 'Ver detalle' }]
      if (item.status === 'PENDIENTE' || item.status === 'APROBADA') {
        actions.push({ key: 'edit', label: 'Editar' })
      }
      if (item.status === 'PENDIENTE') {
        actions.push({ key: 'approve', label: 'Aprobar' })
        actions.push({ key: 'reject',  label: 'Rechazar', danger: true })
      }
      if (item.status === 'APROBADA') {
        actions.push({ key: 'enjoy',  label: 'Marcar como gozada' })
        actions.push({ key: 'cancel', label: 'Cancelar', danger: true })
      }
      if (this.canPay(item)) {
        actions.push({ key: 'pay', label: 'Marcar como pagada' })
      }
      // Anular por error: administrativa, disponible salvo si ya está cancelada/anulada.
      if (item.status !== 'CANCELADA') {
        actions.push({ key: 'annul', label: 'Anular', danger: true })
      }
      return actions
    },
    handleAction(item, action) {
      if (action.key === 'view')    this.$router.push({ name: 'vacation-requests-detail', params: { id: item.id } })
      if (action.key === 'edit')    this.openEdit(item)
      if (action.key === 'approve') this.openApprove(item)
      if (action.key === 'reject')  this.openReject(item)
      if (action.key === 'cancel')  this.openCancel(item)
      if (action.key === 'annul')   this.openAnnul(item)
      if (action.key === 'enjoy')   this.openEnjoy(item)
      if (action.key === 'pay')     this.$router.push({ name: 'vacation-requests-detail', params: { id: item.id }, query: { action: 'pay' } })
    },
    openEdit(item) {
      this.selectedItem = item
      this.editForm = {
        startDate: item.startDate ? moment(item.startDate).format('YYYY-MM-DD') : '',
        endDate: item.endDate ? moment(item.endDate).format('YYYY-MM-DD') : '',
        observations: item.observations || '',
      }
      this.modalEdit = true
    },
    async onEditSubmit() {
      if (!this.editValid || !this.selectedItem) return
      this.modalEdit = false
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.updateRequest(this.selectedItem.id, {
          startDate: this.editForm.startDate,
          endDate: this.editForm.endDate,
          observations: this.editForm.observations,
        })
        Alert.success('Solicitud actualizada correctamente')
        if (resp && resp.payload && resp.payload.warning) Alert.alert('Advertencia', resp.payload.warning)
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al actualizar la solicitud')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    onSearch(val) {
      this.search = val
      this.loadData()
    },
    buildFilterParams() {
      const params = {}
      if (this.search) params.search = this.search
      // Estado específico si se eligió uno; "Todos" (vacío) → sin filtro de estado (backend trae los 5).
      if (this.filters.status) params.status = this.filters.status
      if (this.filters.tipo) params.requestType = this.filters.tipo
      if (this.filters.paid === 'PAID') params.isPaid = true
      else if (this.filters.paid === 'UNPAID') params.isPaid = false
      if (this.filters.companyId) params.companyId = this.filters.companyId
      return params
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.getRequests(this.buildFilterParams())
        this.list = resp.payload || []
        this.selectedIds = []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar solicitudes')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadReport() {
      try {
        this.$store.dispatch('app/loading', true)
        // El export va por POST (body JSON): `statuses` debe ser array (List<String> en el backend).
        // En el GET del listado el CSV lo parte Spring solo, pero Jackson no → se convierte solo aquí.
        const params = this.buildFilterParams()
        if (typeof params.statuses === 'string') params.statuses = params.statuses.split(',')
        const resp = await VacationService.downloadXlsx(params)
        const today = new Date().toISOString().split('T')[0]
        const url = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `solicitudes-vacaciones-${today}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el reporte')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    openApprove(item) { this.selectedItem = item; this.showApprove = true },
    openReject(item)  { this.selectedItem = item; this.rejectReason = ''; this.showReject = true },
    openCancel(item)  { this.selectedItem = item; this.cancelReason = ''; this.showCancel = true },
    openAnnul(item)   { this.selectedItem = item; this.showAnnul = true },
    async doAnnul(reason) {
      try {
        this.annulSubmitting = true
        const resp = await VacationService.annulRequest(this.selectedItem.id, reason)
        Alert.success('Vacación anulada')
        if (resp && resp.payload && resp.payload.warning) Alert.alert('Advertencia', resp.payload.warning)
        this.showAnnul = false
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al anular la vacación')
      } finally {
        this.annulSubmitting = false
      }
    },
    openEnjoy(item)   { this.selectedItem = item; this.showEnjoy = true },
    async confirmEnjoy() {
      this.showEnjoy = false
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.enjoy(this.selectedItem.id)
        Alert.success('Vacaciones marcadas como gozadas')
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al marcar como gozada')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmApprove() {
      this.showApprove = false
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.approveRequest(this.selectedItem.id)
        Alert.success('Solicitud aprobada correctamente')
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al aprobar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmReject() {
      this.showReject = false
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.rejectRequest(this.selectedItem.id, { rejectionReason: this.rejectReason })
        Alert.success('Solicitud rechazada')
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al rechazar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmCancel() {
      this.showCancel = false
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.cancelRequest(this.selectedItem.id, { cancellationReason: this.cancelReason })
        Alert.success('Solicitud cancelada')
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cancelar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    try {
      const resp = await CompanyService.listAll()
      const companyFilter = this.filterOptions.find(o => o.key === 'companyId')
      if (companyFilter) companyFilter.options = (resp.payload || []).map(c => ({ key: c.id, label: c.name }))
    } catch (e) {
      console.error(e)
    }
    this.loadData()
  },
}
</script>

<style lang="stylus" scoped>
.confirm-body
  display flex
  flex-direction column
  align-items center
  text-align center
  gap 8px

.confirm-icon
  width 52px
  height 52px
  border-radius 50%
  display flex
  align-items center
  justify-content center
  font-size 26px
  margin-bottom 4px

  &--success
    background-color rgba(40, 167, 69, 0.12)
    color #28a745

.confirm-question
  font-size 14px
  font-weight 600
  color var(--admin-text-main)
  margin 0

.confirm-detail
  font-size 13px
  color var(--admin-text-muted)
  margin 0
</style>
